"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { LOCALES } from "@/lib/i18n/locale";
import { blogHref } from "@/app/(site)/[locale]/blog/data/paths";
import { m, useReducedMotion, type Variants } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { SUBSCRIPTIONS, thb } from "@/lib/pricing/catalog";
import LanguageSwitcher from "@/components/language-switcher";
import { useTranslation } from "@/lib/hooks/use-translation";
import { PRICING_PANEL_EVENT, type PanelId } from "@/components/home/pricing-panel";

/**
 * Where a cross-page menu tap wants to land, parked between the two pages.
 *
 * The fragment cannot ride in the URL — that is the whole point — and it
 * cannot ride in React state either, because the navigation unmounts the page
 * that would hold it. `sessionStorage` outlives exactly that gap and nothing
 * more.
 */
const PENDING_SCROLL = "nav:scroll-to";

/**
 * Which run of {@link runPendingScroll} owns the retry loop. A later call
 * supersedes an earlier one instead of the two fighting over the scroll.
 */
let scrollRun = 0;

/**
 * Scroll to the section a cross-page menu tap asked for, once the destination
 * can actually be reached.
 *
 * Deliberately outside React. As an effect this never finished: the effect
 * re-runs while it is still working — `useReducedMotion` reports `null` on the
 * first render and a real answer on the next — and the cleanup cancelled the
 * animation frame every time, so the loop was killed on a schedule that had
 * nothing to do with whether it had done its job. Out here it owns its own
 * lifetime, bounded by the deadline below.
 */
function runPendingScroll(pathname: string, instant: boolean) {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(PENDING_SCROLL);
  } catch {
    // Storage can throw outright (private mode, blocked site data). No stored
    // target simply means an ordinary page load.
    return;
  }
  if (!stored) return;

  const cut = stored.indexOf("|");
  const path = stored.slice(0, cut);
  const id = stored.slice(cut + 1);

  const forget = () => {
    try {
      sessionStorage.removeItem(PENDING_SCROLL);
    } catch {
      // Nothing to clean up if storage is unavailable.
    }
  };

  // Landed somewhere else in the meantime — drop it rather than scrolling a
  // page that never asked for it.
  if (path !== pathname) {
    forget();
    return;
  }

  const run = ++scrollRun;
  let seen = false;

  /**
   * Try to land on the section. Returns false while that is not yet possible,
   * which is a different thing from having failed.
   *
   * Waiting on the element existing is not enough: for the first frames after
   * a route change the page below it has not laid out, the container is barely
   * taller than the viewport, and a scroll to 2148 clamps to about 4 —
   * indistinguishable from never having scrolled at all. So the offset has to
   * be reachable, not merely computable.
   */
  const land = (force: boolean) => {
    const el = document.getElementById(id);
    const box = document.getElementById("scroll-container");
    if (!el || !box) return false;
    seen = true;

    // `scroll-mt-13` is what clears the fixed header for the browser's own
    // anchor jump; a hand-rolled scroll has to read it and subtract it.
    const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    const top =
      el.getBoundingClientRect().top -
      box.getBoundingClientRect().top +
      box.scrollTop -
      margin;

    if (!force && box.scrollHeight - box.clientHeight < top - 1) return false;

    forget();
    // A hidden tab does not animate: a smooth scroll issued there sits at zero
    // until the reader comes back, and then jumps. Nobody is watching the
    // animation anyway, so skip it and be in the right place on return.
    box.scrollTo({
      top,
      behavior: instant || document.hidden ? "instant" : "smooth",
    });
    return true;
  };

  // Generous, because the ceiling is only ever reached on a cold route: in dev
  // the first visit compiles the page, and on a slow connection the sections
  // below arrive late. A tight bound gives up mid-compile and strands the
  // reader at the top, which is the bug this replaced.
  const until = performance.now() + 6000;
  const look = () => {
    if (run !== scrollRun) return;
    if (land(false)) return;
    if (performance.now() < until) {
      // A timer rather than an animation frame. Frames do not fire at all in a
      // hidden tab, so a reader who opened the page in a background tab — or
      // switched away while it loaded — would come back to the top of it, with
      // the retry loop never having run once.
      window.setTimeout(look, 50);
      return;
    }
    // Out of time. Land where we can if the section is at least present — a
    // clamped scroll beats none — and otherwise put the reader at the top of
    // the page they asked for, since the router was told not to.
    const reached = seen && land(true);
    forget();
    if (!reached) document.getElementById("scroll-container")?.scrollTo({ top: 0 });
  };

  look();
}

type NavItem = {
  /** Dotted key into the `nav` namespace, not the copy itself — the drawer
      renders in whichever language the server chose. */
  labelKey: string;
  href: string;
  /** Which pricing tab to select on arrival — see `PRICING_PANEL_EVENT`. */
  panel?: PanelId;
  children?: NavItem[];
  /** A child that titles the group under it rather than being a peer of it.
      Only the Product menu has these — the two product names. */
  heading?: boolean;
};

/**
 * The mobile drawer's menu, in comp order. Every entry is an in-page anchor:
 * the sections carry matching ids and a `scroll-mt-13` that clears the fixed
 * header, so a plain `<a>` lands them correctly with no scroll handler.
 *
 * There is no desktop equivalent yet — the comp for this menu is a phone
 * frame, so the hamburger and the drawer are both `lg:hidden` and the desktop
 * bar stays the bare logo / language / CTA row it already was.
 */
const MENU: NavItem[] = [
  { labelKey: "nav.menu.home", href: "/" },
  {
    labelKey: "nav.menu.product",
    href: "/product/crm",
    children: [
      { labelKey: "nav.product.productCrm", href: "/product/crm", heading: true },
      { labelKey: "nav.product.crmSystem", href: "/product/crm#crm" },
      { labelKey: "nav.product.reservation", href: "/product/crm#reservation" },
      { labelKey: "nav.product.productPos", href: "/product/pos", heading: true },
      { labelKey: "nav.product.posSystem", href: "/product/pos#pos" },
      { labelKey: "nav.product.selfOrder", href: "/product/pos#self-order" },
    ],
  },
  // Absolute, not bare fragments. The drawer opens on the product pages too
  // now, and `#features` there matches nothing — the reader taps and the page
  // sits still. `/#features` takes them to the section instead. On the home
  // page `onNavigate` still finds the element by its fragment and scrolls, so
  // nothing about the behaviour there changes.
  { labelKey: "nav.menu.features", href: "/#features" },
  { labelKey: "nav.menu.featureBento", href: "/#feature-bento" },
  { labelKey: "nav.menu.whyUs", href: "/#why-us" },
  {
    labelKey: "nav.menu.pricing",
    href: "/#pricing",
    children: [
      // The `panel` event only has a listener on the home page's pricing rail,
      // so tapping one of these from a product page lands on the section with
      // its default tab rather than the chosen one. Worth a follow-up if the
      // product pages start carrying real traffic to these rows.
      { labelKey: "nav.menu.pricingCrm", href: "/#pricing", panel: "crm" },
      {
        labelKey: "nav.menu.pricingPosMonthly",
        href: "/#pricing",
        panel: "pos-monthly",
      },
      { labelKey: "nav.menu.pricingPosKit", href: "/#pricing", panel: "pos-kit" },
    ],
  },
  { labelKey: "nav.menu.faq", href: "/#faq" },
  // A route of its own, not a section — `onNavigate` lets a fragment-less href
  // fall through to the router.
  { labelKey: "nav.menu.blog", href: "/blog" },
];

/** The places the bar names — each one lights up while the reader is in it. */
type Section = "home" | "product" | "blog";

/**
 * Which place a path belongs to, or null for anywhere the bar doesn't name
 * (the legal pages) and for in-page anchors like `/#pricing`, which are
 * sections of the home page rather than places of their own.
 *
 * The locale is stripped first: the proxy rewrites `/blog` onto `/th/blog`,
 * and both spellings have to give the same answer, or the server and the
 * browser could disagree about which row is lit.
 */
function sectionOf(path: string): Section | null {
  const [, first, ...rest] = path.split("/");
  const clean = (LOCALES as readonly string[]).includes(first)
    ? `/${rest.join("/")}`
    : path;
  if (clean === "/") return "home";
  if (clean === "/product" || clean.startsWith("/product/")) return "product";
  if (clean === "/blog" || clean.startsWith("/blog/")) return "blog";
  return null;
}

/** The desktop bar's rows: quiet by default, the comp's blue on the current place. */
const BAR_ROW = "text-[15px]/[24px] transition-colors";
const BAR_IDLE = "text-black/70 hover:text-black";
const BAR_ACTIVE = "font-semibold text-[#0084EB]";

/**
 * A bar label that keeps its width when its row becomes active.
 *
 * The active row is set semibold, and a label that grew by a few pixels would
 * nudge the centred menu sideways on every navigation — the thing the bar's
 * three-track grid exists to prevent. An invisible semibold copy stacked in
 * the same cell holds the wider size all the time.
 */
function BarLabel({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <span className="inline-grid justify-items-center">
      <span className="col-start-1 row-start-1">{children}</span>
      <span aria-hidden className="invisible col-start-1 row-start-1 font-semibold">
        {children}
      </span>
    </span>
  );
}

/** The drawer's own CTA. Not a menu row, but the same kind of in-page jump,
    so it goes through the same handler rather than its own. */
const CONTACT_ITEM: NavItem = { labelKey: "nav.cta.trial", href: "/#contact" };

/** The two columns of the Product panel, and the sections each one offers. */
const PRODUCT_COLUMNS = [
  {
    titleKey: "nav.product.productCrm",
    blurbKey: "nav.product.productCrmDesc",
    href: "/product/crm",
    seeKey: "nav.product.seeCrm",
    fromKey: "nav.product.promoCrm",
    from: SUBSCRIPTIONS.crmStarter,
    items: [
      { labelKey: "nav.product.crmSystem", descKey: "nav.product.crmSystemDesc", href: "/product/crm#crm" },
      { labelKey: "nav.product.reservation", descKey: "nav.product.reservationDesc", href: "/product/crm#reservation" },
    ],
  },
  {
    titleKey: "nav.product.productPos",
    blurbKey: "nav.product.productPosDesc",
    href: "/product/pos",
    seeKey: "nav.product.seePos",
    fromKey: "nav.product.promoPos",
    from: SUBSCRIPTIONS.posMonthly,
    items: [
      { labelKey: "nav.product.posSystem", descKey: "nav.product.posSystemDesc", href: "/product/pos#pos" },
      { labelKey: "nav.product.selfOrder", descKey: "nav.product.selfOrderDesc", href: "/product/pos#self-order" },
    ],
  },
] as const;

/** Where the reveal circle grows from, measured rather than assumed. */
type Reveal = { x: number; y: number; r: number };

/**
 * The drawer is uncovered by a circle expanding out of the hamburger itself,
 * so the panel reads as coming *from* the control the reader just pressed.
 *
 * The origin is measured (see `useReveal`) instead of hard-coded, because the
 * toggle sits at the end of a flex row whose width depends on the viewport and
 * on the scrollbar the header reserves — and because it sits in the header,
 * *above* the panel, which puts the circle's centre at a negative y.
 */
function makePanelVariants(reduced: boolean): Variants {
  if (reduced) {
    // A circle sweeping across the whole screen is exactly the kind of large
    // motion this preference asks us to drop, so the panel just fades.
    return {
      open: {
        opacity: 1,
        clipPath: "none",
        transition: { duration: 0.2, staggerChildren: 0 },
      },
      closed: { opacity: 0, clipPath: "none", transition: { duration: 0.15 } },
    };
  }

  return {
    open: ({ x, y, r }: Reveal) => ({
      opacity: 1,
      clipPath: `circle(${r}px at ${x}px ${y}px)`,
      transition: {
        type: "spring",
        stiffness: 20,
        restDelta: 2,
        // The items ride in behind the leading edge of the circle rather than
        // with it, so the panel is already ground by the time they arrive.
        delayChildren: 0.2,
        staggerChildren: 0.07,
      },
    }),
    closed: ({ x, y }: Reveal) => ({
      opacity: 1,
      clipPath: `circle(0px at ${x}px ${y}px)`,
      transition: {
        // Held just long enough for the items to have cleared out first.
        delay: 0.2,
        type: "spring",
        stiffness: 400,
        damping: 40,
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    }),
  };
}

function makeItemVariants(reduced: boolean): Variants {
  if (reduced) return { open: { opacity: 1 }, closed: { opacity: 1 } };
  return {
    open: {
      y: 0,
      opacity: 1,
      transition: { y: { stiffness: 1000, velocity: -100 } },
    },
    closed: {
      y: 50,
      opacity: 0,
      transition: { y: { stiffness: 1000 } },
    },
  };
}

/**
 * Measures the circle that has to cover the panel from the toggle's centre.
 *
 * Radius is the distance to the panel's furthest corner — the toggle is near
 * the top-right, so that is normally the bottom-left, but it is cheaper to
 * check all four than to reason about which.
 */
function useReveal(
  toggleRef: React.RefObject<HTMLButtonElement | null>,
  panelRef: React.RefObject<HTMLDivElement | null>,
  isOpen: boolean,
) {
  const [reveal, setReveal] = useState<Reveal>({ x: 0, y: 0, r: 0 });

  useEffect(() => {
    const measure = () => {
      const toggle = toggleRef.current?.getBoundingClientRect();
      const panel = panelRef.current?.getBoundingClientRect();
      if (!toggle || !panel) return;

      const x = toggle.left + toggle.width / 2 - panel.left;
      const y = toggle.top + toggle.height / 2 - panel.top;
      const r = Math.ceil(
        Math.max(
          Math.hypot(x, y),
          Math.hypot(panel.width - x, y),
          Math.hypot(x, panel.height - y),
          Math.hypot(panel.width - x, panel.height - y),
        ),
      );

      setReveal((prev) =>
        prev.x === x && prev.y === y && prev.r === r ? prev : { x, y, r },
      );
    };

    measure();
    window.addEventListener("resize", measure);
    // The address bar collapsing on mobile changes the panel's height without
    // firing `resize` on every browser; the toggle's own box never moves, so
    // watching the panel is enough.
    const observer = new ResizeObserver(measure);
    if (panelRef.current) observer.observe(panelRef.current);
    return () => {
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
    // Re-measured on every toggle, not just on mount: the header slides down
    // from y:-24 on first paint, so a mount-time read catches the toggle
    // mid-entrance and puts the circle's centre ~24px too high. By the time
    // anyone presses it the header has settled.
  }, [toggleRef, panelRef, isOpen]);

  return reveal;
}

/**
 * The hamburger, which folds into the drawer's close button rather than being
 * swapped for a different icon: the top and bottom bars rotate into the X and
 * the middle one fades, so the control stays the same object throughout.
 *
 * Drawn as three stroked paths instead of the comp's three filled rectangles
 * because that is what makes the fold animatable — the geometry matches
 * (18x2 bars, 4px apart, 1px caps, #0086F0), and the X the open state lands on
 * spans the same 5..19 box the comp's two rotated bars do.
 */
function MenuIcon() {
  const line = {
    fill: "transparent",
    strokeWidth: 2,
    stroke: "#0086F0",
    strokeLinecap: "round",
  } as const;

  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden focusable="false">
      <m.path
        {...line}
        variants={{
          closed: { d: "M 3 6 L 21 6" },
          open: { d: "M 5 5 L 19 19" },
        }}
      />
      <m.path
        {...line}
        d="M 3 12 L 21 12"
        variants={{ closed: { opacity: 1 }, open: { opacity: 0 } }}
        transition={{ duration: 0.1 }}
      />
      <m.path
        {...line}
        variants={{
          closed: { d: "M 3 18 L 21 18" },
          open: { d: "M 5 19 L 19 5" },
        }}
      />
    </svg>
  );
}

export default function Navbar() {
  const { t, language } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const section = sectionOf(pathname);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const reveal = useReveal(toggleRef, panelRef, isMobileMenuOpen);

  const reduced = useReducedMotion();
  const panelVariants = makePanelVariants(Boolean(reduced));
  const itemVariants = makeItemVariants(Boolean(reduced));

  // The page scrolls inside #scroll-container, whose scrollbar sits at the
  // viewport's right edge. The fixed header spans the full viewport width, so
  // it would paint over that scrollbar. Stop the header where the scrollbar
  // begins by reserving its measured width on the right.
  useEffect(() => {
    const measure = () => {
      const el = document.getElementById("scroll-container");
      if (el) setScrollbarWidth(el.offsetWidth - el.clientWidth);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Escape closes the drawer, which is the one way out a full-screen overlay
  // owes a keyboard user beyond the toggle itself.
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMobileMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isMobileMenuOpen]);

  // The other half of a cross-page menu tap: the route has settled, so hand
  // the destination to the runner above.
  useEffect(() => {
    runPendingScroll(pathname, reduced === true);
  }, [pathname, reduced]);

  /**
   * Menu rows stay ordinary anchors — they work with JS off, they can be
   * opened in a new tab, and assistive tech still reads them as links — but a
   * plain tap is intercepted and scrolled by hand, so the fragment never
   * reaches the address bar. `/#why-us` sitting in the URL is a bookmarkable
   * location the reader never asked to create; the tap only meant "take me
   * down the page".
   *
   * `scrollIntoView` is what makes that swap free: it honours the sections'
   * `scroll-mt-13`, exactly as the browser's own anchor jump did, so the
   * heading still clears the fixed header. It also walks up to
   * `#scroll-container` on its own — the page does not scroll on `body`.
   *
   * The rest is unchanged: close the drawer, and tell the pricing section
   * which tab the reader asked for.
   */
  const onNavigate = (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: NavItem,
  ) => {
    if (item.panel) {
      window.dispatchEvent(
        new CustomEvent(PRICING_PANEL_EVENT, { detail: item.panel }),
      );
    }
    setIsMobileMenuOpen(false);

    // A modified click is a request for a new tab or window. Leave those to
    // the browser — hash and all, since that URL is the whole point of them.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    // `#faq` and `/#faq` should both scroll when that section is on this page;
    // only the fragment identifies it. A row with no fragment at all (the
    // product pages) is a route, and falls through to the browser.
    const hash = item.href.includes("#")
      ? item.href.slice(item.href.indexOf("#") + 1)
      : "";
    const target = hash ? document.getElementById(hash) : null;

    if (!target) {
      // The section is on another page. Route there by path alone and scroll
      // on arrival, rather than letting the anchor carry the fragment — the
      // reader asked to be taken to a place, not to mint a URL, and `#crm`
      // would sit in the address bar long after it had done its job.
      const path = hash ? item.href.slice(0, item.href.indexOf("#")) || "/" : item.href;
      // Same page and still no element: the id is genuinely absent, so leave
      // the click to the browser rather than swallowing it into nothing.
      if (!hash || path === pathname) return;

      e.preventDefault();
      try {
        sessionStorage.setItem(PENDING_SCROLL, `${path}|${hash}`);
      } catch {
        // Without storage the reader lands at the top of the right page,
        // which is a worse arrival but not a broken one.
      }
      // `scroll: false` because the router's own jump to the top lands after
      // this component's, and would undo it. Owning the scroll means owning
      // the miss too, which is what the fallback below the deadline is for.
      router.push(path, { scroll: false });
      return;
    }

    e.preventDefault();
    target.scrollIntoView({
      // The container carries `scroll-behavior: smooth`, which "auto" would
      // inherit — so the reduced-motion case has to say "instant" outright.
      behavior: reduced ? "instant" : "smooth",
      block: "start",
    });
  };

  return (
    <div>
      <m.header
        initial={{ y: -24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="fixed left-0 top-0 z-[100] flex justify-center"
        style={{ right: scrollbarWidth }}
      >
        {/* Straight off the comp: white at 90%, and a hairline along the
            bottom edge only — not the four-sided border this bar used to
            carry. `h-13` states the height the comp's 12px padding and 29px
            lockup already come to, because the scroll container's `pt-13` and
            the two sticky sections' `top-13` are all measured against it and
            must not drift when the contents change. */}
        {/* From `lg`, three tracks rather than a `justify-between` row: the
            menu is centred on the BAR, so it has to be its own column with
            equal-weight tracks either side. In a flex row it would centre on
            whatever space the lockup and the actions happened to leave, and
            drift every time one of them changed width — the language switcher
            alone does that between th and en.

            Below `lg` it is a plain flex row, because the grid does not
            degrade to one. The middle column is `hidden` — `display: none`,
            which takes it out of the grid entirely rather than leaving an
            empty track — so the actions were auto-placed into the *middle*
            track and sat mid-bar with the third track empty on the right, and
            the `1fr` the lockup was left with was narrower than the lockup, so
            the wordmark overflowed its own box and ran under the flag. Two
            items and `justify-between` is what that row actually wants. */}
        <nav className="flex h-13 w-full items-center justify-between gap-4 border-b border-[#E5E7EA] bg-white/90 px-4 backdrop-blur-md md:px-[80px] lg:grid lg:grid-cols-[1fr_auto_1fr]">
          <Link href="/" className="flex w-fit shrink-0 items-center gap-[13px]">
            <Image
              src="/v2/logo.webp"
              alt="Jelloducks Logo"
              width={29}
              height={29}
              className="h-[29px] w-[29px] object-contain"
            />
            {/* 21/32 rather than the comp's literal 20.63/31.54 — the lockup
                was exported from a frame scaled ~1.29x, and the fractions are
                that artifact rather than a decision. */}
            <span className="text-[21px]/[32px] font-bold tracking-tight text-gray-900">
              Jelloducks
            </span>
          </Link>

          {/* Places only. The home page's own sections used to sit here too,
              but they are anchors into one page rather than places, and a bar
              that is on every route reads better naming the things a shopper
              is actually choosing between. They are still in the drawer, and
              still linked from the pages themselves. The blog is a place of
              its own, so it earns a row. */}
          <div className="hidden items-center gap-[26px] lg:flex">
            {/* The lockup already goes home, but a named row is what a reader
                on a product page looks for to get back. */}
            <Link
              href="/"
              aria-current={section === "home" ? "page" : undefined}
              className={`${BAR_ROW} ${section === "home" ? BAR_ACTIVE : BAR_IDLE}`}
            >
              <BarLabel>{t("nav.menu.home")}</BarLabel>
            </Link>
            <DesktopProductMenu
              onNavigate={onNavigate}
              active={section === "product"}
            />
            {/* The blog's language is in its address (proxy.ts), so the row
                goes to the one the reader is reading in. */}
            <Link
              href={blogHref(language, "/blog")}
              aria-current={section === "blog" ? "page" : undefined}
              className={`${BAR_ROW} ${section === "blog" ? BAR_ACTIVE : BAR_IDLE}`}
            >
              <BarLabel>{t("nav.menu.blog")}</BarLabel>
            </Link>
          </div>

          <div className="flex items-center justify-end gap-1">
            {/* The switcher is what the bar carries at every width. On mobile
                it stands where the comp draws the CTA, because the drawer
                already has that CTA at its foot and two of the same button a
                tap apart is one too many. */}
            <LanguageSwitcher />

            {/* Jumps to the contact section at the foot of the home page
                rather than to a /contact route — v2 has no such page, and the
                form the CTA is after is already down there. A real anchor, not
                a button with a scroll handler, so it survives keyboard,
                middle-click and no-JS; `asChild` lets it keep the button's
                looks. `data-track` still reports to the CMS dashboard — the
                tracker listens on any `[data-track]` ancestor, element type
                doesn't matter.

                Desktop only. On mobile the switcher has the slot beside the
                hamburger and the CTA lives at the foot of the drawer, which
                is also the one place the comp's open header shows it.

                Straight off the comp: #0084EB at a 12px radius, rather than
                the pill in `--primary-strong` the rest of the site's CTAs
                wear. The button's own size variant already lands the comp's
                box — 16/8 padding, 8px gap, 14px/20px at weight 500 — so only
                the fill and the corner are stated here.

                The fill is written literally instead of promoted to a token
                because it is *not* `--primary-strong`: that tone was picked to
                clear 4.5:1 on white (see the note in globals.css) and this one
                measures 3.82:1, so pointing the token at it would drag every
                other CTA below AA too. Kept local until the comp says the
                whole system moves. */}
            <Button
              asChild
              data-track="nav_contact"
              className="hidden rounded-sm bg-primary-strong transition-transform hover:scale-[1.03] hover:bg-primary-strong/90 lg:inline-flex"
            >
              {/* Absolute for the same reason as the menu rows above: the bar
                  is on every page, and only the home page has `#contact`. It
                  goes through `onNavigate` too, so on the home page it still
                  scrolls without putting the fragment in the address bar. */}
              <Link href="/#contact" onClick={(e) => onNavigate(e, CONTACT_ITEM)}>
                {t("nav.cta.signup")}
              </Link>
            </Button>

            <m.button
              ref={toggleRef}
              type="button"
              initial={false}
              animate={isMobileMenuOpen ? "open" : "closed"}
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={t(isMobileMenuOpen ? "nav.toggle.close" : "nav.toggle.open")}
              className="ml-2 grid size-6 place-items-center lg:hidden"
            >
              <MenuIcon />
            </m.button>
          </div>
        </nav>
      </m.header>

      {/* The drawer starts below the header rather than covering it, so the
          lockup and the toggle stay put and only the body of the screen
          changes — which is what the comp's two frames show.

          It stays mounted so the circle can close as well as open; `inert`
          takes it out of the tab order and off the accessibility tree in
          lockstep with the animation, and `pointer-events` follows the same
          flag so a closed panel cannot swallow taps meant for the page. */}
      <m.div
        ref={panelRef}
        id="mobile-menu"
        initial={false}
        animate={isMobileMenuOpen ? "open" : "closed"}
        custom={reveal}
        variants={panelVariants}
        inert={!isMobileMenuOpen}
        style={{ pointerEvents: isMobileMenuOpen ? "auto" : "none" }}
        className="fixed inset-x-0 bottom-0 top-13 z-[99] flex flex-col justify-between gap-8 overflow-y-auto bg-[#FCFCFC] px-5 pb-12 pt-8 lg:hidden"
      >
        <m.ul className="flex flex-col items-start gap-10">
          {MENU.map((item) => {
            // Only a row that names a place can be current; the in-page
            // anchor rows resolve to null and stay black on the home page.
            const current = section !== null && sectionOf(item.href) === section;
            return (
            <m.li
              key={item.labelKey}
              variants={itemVariants}
              className="flex flex-col items-start gap-6"
            >
              <Link
                href={item.href === "/blog" ? blogHref(language, item.href) : item.href}
                onClick={(e) => onNavigate(e, item)}
                aria-current={current ? "page" : undefined}
                className={`text-[20px]/[28px] font-semibold ${current ? "text-[#0084EB]" : "text-black"}`}
              >
                {t(item.labelKey)}
              </Link>

              {item.children && (
                <div className="flex flex-col items-start gap-5 pl-[15px]">
                  {item.children.map((child) =>
                    // A product name is a link like any other row, but it is
                    // also the label for the two rows under it — so it is set
                    // smaller and quieter, and its sections are indented past
                    // it rather than sitting level with it.
                    child.heading ? (
                      <Link
                        key={child.labelKey}
                        href={child.href}
                        onClick={(e) => onNavigate(e, child)}
                        className="pt-1 text-[15px]/[20px] font-semibold text-black/45 first:pt-0"
                      >
                        {t(child.labelKey)}
                      </Link>
                    ) : (
                      <Link
                        key={child.labelKey}
                        href={child.href}
                        onClick={(e) => onNavigate(e, child)}
                        className="pl-3 text-[17px]/[22px] font-normal text-black"
                      >
                        {t(child.labelKey)}
                      </Link>
                    ),
                  )}
                </div>
              )}
            </m.li>
            );
          })}
        </m.ul>

        {/* `flex: 1 1 0` in the comp, i.e. the button takes the whole row —
            this is the only CTA a mobile reader gets from the header, the bar
            itself having given the slot to the switcher. */}
        <m.div variants={itemVariants} className="flex w-full items-center gap-2">
          <Button
            asChild
            data-track="nav_menu_contact"
            className="h-auto flex-1 rounded-[14px] bg-primary-strong px-5 py-3 text-[18px]/[24px] font-medium hover:bg-primary-strong/90"
          >
            <a
              href="#contact"
              onClick={(e) => onNavigate(e, CONTACT_ITEM)}
            >
              {t("nav.cta.trial")}
            </a>
          </Button>
        </m.div>
      </m.div>
    </div>
  );
}

/**
 * The desktop Product menu.
 *
 * A real button with `aria-expanded`, not a hover-only panel: a hover menu is
 * unreachable by keyboard and unusable by touch, and this bar is the only way
 * into the product pages. Pointer users still get hover — entering the wrapper
 * opens it and leaving closes it — but the panel can equally be opened with
 * Enter, walked with Tab, and dismissed with Escape.
 *
 * Closing on `blur` is done by asking where focus went: `relatedTarget` inside
 * the wrapper means the reader tabbed from the trigger into the panel, which
 * is not a reason to shut it.
 */
function DesktopProductMenu({
  onNavigate,
  active,
}: Readonly<{
  onNavigate: (e: React.MouseEvent<HTMLAnchorElement>, item: NavItem) => void;
  /** The reader is on one of the product pages — light the trigger. */
  active: boolean;
}>) {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);

  /**
   * Hovering the trigger opens the panel and *leaves* it open — a mouse
   * arriving is treated as a press, not as a state the panel has to be held
   * in. Moving the pointer away does nothing, so a reader can cross the gap to
   * the panel, or look away and back, without it vanishing under them.
   *
   * That means there is only one flag: hover and click both simply open or
   * toggle it, and it stays until something deliberate shuts it. There are
   * four such things, which is what stops a 920px panel from being stuck over
   * the page: clicking the trigger again, Escape, choosing a row, or a click
   * anywhere outside.
   */
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    // A tap or click anywhere else shuts it. This is what closes the panel on
    // a touch screen, where `pointerleave` may never arrive.
    const onDown = (e: PointerEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node | null)) close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [isOpen, close]);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      // Mouse only. A finger has no hover, and the compatibility `pointerenter`
      // a tap emits would otherwise open the panel a moment before the `click`
      // toggled it straight back shut — which is how a touch user ends up
      // unable to open it at all. On touch the click below is the way in.
      onPointerEnter={(e) => {
        if (e.pointerType === "mouse") setIsOpen(true);
      }}
      onBlur={(e) => {
        // Focus moving from the trigger into the panel is not a reason to shut
        // it — only focus leaving the whole menu is.
        if (!wrapperRef.current?.contains(e.relatedTarget as Node | null)) close();
      }}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={() => setIsOpen((open) => !open)}
        // It used to be blue on every page, which read as "you are here" even
        // on the home page and the blog. Now it follows the other rows.
        className={`flex items-center gap-1.5 ${BAR_ROW} ${active ? BAR_ACTIVE : BAR_IDLE}`}
      >
        <BarLabel>{t("nav.menu.product")}</BarLabel>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
          className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        >
          {/* currentColor, so the chevron follows the label's state. */}
          <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Kept mounted so it can animate shut as well as open; `inert` takes it
          out of the tab order in lockstep, and `pointer-events` follows the
          same flag so a closed panel cannot swallow clicks on the page. */}
      <m.div
        inert={!isOpen}
        initial={false}
        animate={isOpen ? { opacity: 1, y: 0 } : { opacity: 0, y: -8 }}
        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
        style={{ pointerEvents: isOpen ? "auto" : "none" }}
        // Centred on the trigger rather than flush to its left edge: the
        // trigger sits mid-bar now, and a 920px panel opening rightwards from
        // there would run off the screen. `max-w` is the backstop at the
        // narrowest width this menu is shown at (lg, 1024px).
        className="absolute left-1/2 top-full z-50 mt-3 w-[920px] max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-[20px] border border-[#E5E7EA] bg-white p-7 shadow-[0_24px_56px_-20px_rgba(15,23,42,0.28)]"
      >
        <div className="grid grid-cols-[1fr_1fr_268px] gap-7">
          {PRODUCT_COLUMNS.map((column, i) => (
            <div
              key={column.href}
              className={`flex flex-col gap-3.5 ${i === 1 ? "border-l border-[#F0F2F5] pl-7" : ""}`}
            >
              <Link
                href={column.href}
                onClick={(e) => onNavigate(e, { labelKey: column.titleKey, href: column.href })}
                className="flex flex-col gap-0.5"
              >
                <span className="text-[17px]/[24px] font-semibold text-[#16202E]">
                  {t(column.titleKey)}
                </span>
                <span className="text-[13px]/[20px] text-black/50">{t(column.blurbKey)}</span>
              </Link>

              <div className="flex flex-col gap-1.5">
                {column.items.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={(e) => {
                      close();
                      onNavigate(e, { labelKey: item.labelKey, href: item.href });
                    }}
                    className="rounded-xl px-3.5 py-3 transition-colors hover:bg-[#F4F9FE]"
                  >
                    <span className="block text-[15px]/[22px] font-semibold text-[#16202E]">
                      {t(item.labelKey)}
                    </span>
                    <span className="block text-[13px]/[20px] text-black/55">
                      {t(item.descKey)}
                    </span>
                  </Link>
                ))}
              </div>

              <Link
                href={column.href}
                onClick={(e) => onNavigate(e, { labelKey: column.seeKey, href: column.href })}
                className="px-3.5 text-[14px]/[22px] font-semibold text-primary-strong hover:underline"
              >
                {t(column.seeKey)} →
              </Link>
            </div>
          ))}

          {/* The rail is the menu's only piece of selling: the two systems are
              priced separately and are better together, which is the one thing
              a reader choosing between them needs told. */}
          <div className="flex flex-col justify-between gap-4 rounded-2xl bg-[linear-gradient(150deg,#F4F9FE_0%,#E8F3FD_100%)] p-5">
            <div>
              <p className="mb-1.5 text-[16px]/[24px] font-semibold text-[#16202E]">
                {t("nav.product.promoTitle")}
              </p>
              <p className="text-[13px]/[21px] text-black/60">{t("nav.product.promoBody")}</p>
            </div>
            <div className="flex flex-col gap-2">
              {PRODUCT_COLUMNS.map((column) => (
                <p
                  key={column.fromKey}
                  className="flex items-baseline justify-between rounded-[10px] bg-white/80 px-3 py-2"
                >
                  <span className="text-[13px]/[20px] text-black/60">{t(column.fromKey)}</span>
                  <span className="text-[15px]/[22px] font-bold text-[#16202E]">
                    {thb(column.from)}
                    <span className="text-[11px] font-medium text-black/50">
                      {t("nav.product.perBranch")}
                    </span>
                  </span>
                </p>
              ))}
              <Link
                href="/#contact"
                onClick={(e) => onNavigate(e, { labelKey: "nav.cta.trial", href: "/#contact" })}
                data-track="nav_product_trial"
                className="inline-flex h-10 items-center justify-center rounded-xl bg-primary-strong text-[14px]/[20px] font-semibold text-white"
              >
                {t("nav.cta.trial")}
              </Link>
            </div>
          </div>
        </div>
      </m.div>
    </div>
  );
}
